import {inject} from 'aurelia-framework';
import {DialogController, DialogService} from 'aurelia-dialog';
import EditorContent from './utils/editor-content';
import {Client} from '../api/client';
import {Confirm} from '../dialog/confirm';
import {FlashService} from '../flash/flash-service';
import {EventAggregator} from 'aurelia-event-aggregator';

import './editor-modal.less';
import {UnsavedChanges} from '../form/unsaved-changes-prevent';
import {WebsiteContextClient} from './utils/website-context-client';
import queryString from '../utilities/query-string';
import {loadGrapeJsEditor, destroyGrapeJsEditor, getGrapeJsProjectData, setGrapeJsAutosave} from './grapejs/loadGrapesJsEditor';
import autosaveStore from './grapejs/services/autosaveStore';

@inject(
    DialogController,
    DialogService,
    Client,
    FlashService,
    EventAggregator,
    WebsiteContextClient
)
export default class EditorModal {
    label;
    context;
    blocks;
    data;
    editorId;
    divId;
    reference;

    previewWindow;
    previousContext;
    CONST_IS_GRAPE_JS = false;
    grapeJsLoading = false;

    saveOnlyAction = {
        buttonClass: 'tw-btn-default btn-sm',
        label: 'Speichern'
    }

    previewAction = {
        buttonClass: "tw-btn-info",
        label: "Vorschau",
        type: 'workflow',
    };

    previewUpdateAction = {
        buttonClass: "tw-btn-info",
        label: "Vorschau aktualisieren",
        type: 'workflow',
    };

    constructor(
        dialog,
        dialogService,
        client,
        flash,
        ea,
        websiteContextClient
    ) {
        this.dialog = dialog;
        this.dialogService = dialogService;
        this.client = client;
        this.flash = flash;
        this.ea = ea;
        this.websiteContextClient = websiteContextClient;
    }

    activate = async ({
                          label,
                          context,
                          blocks,
                          content,
                          editor,
                          formId,
                          modelId,
                          id,
                          property,
                          formConfig,
                          reference,
                          contextModelId,
                          data,
                          public: p,
                      }) => {
        this.label = label || 'Editor';
        this.context = context;
        this.blocks = blocks;
        this.content = content;
        this.editor = editor;
        this.formId = formId;
        this.id = id;
        this.modelId = modelId;
        this.property = property;
        this.formConfig = formConfig;
        this.editorId = ++EditorContent.id;
        this.data = data;
        this.website = await this.websiteContextClient.getWebsite(this.data?.website)
        this.CONST_IS_GRAPE_JS = this.website?.useGrapesEditor ?? false;
        this.divId = 'gutenberg__editor_' + this.editorId;

        this.reference = reference;

        this.saveAction = {
            buttonClass: 'tw-btn-danger btn-sm action-ok',
            label: this.CONST_IS_GRAPE_JS ? 'Speichern & Schließen' : 'form.save'
        }

        EditorContent.reference = reference;
        EditorContent.contextModelId = contextModelId;

        let endpoint = null;
        let workflowFormId = null;

        switch (EditorContent.reference.modelId) {
            case 'cms/content-page':
                endpoint = 'cms/content-page-editor-preview';
                break;

            case 'cms/landing-page-layout':
                endpoint = 'cms/content-page-editor-preview';
                break;

            case 'cms/email-template':
                this.CONST_IS_GRAPE_JS = false;
                endpoint = 'cms/email-template-object-preview';
                workflowFormId = 'cms/object-preview';
                break;

            case 'cms/pdf-document':
                this.CONST_IS_GRAPE_JS = false;
                endpoint = 'cms/pdf-template-object-preview';
                workflowFormId = 'cms/object-preview';
                break;

            case 'email-campaign/campaign':
                this.CONST_IS_GRAPE_JS = false;
                endpoint = 'email-campaign/email-campaign-preview';
                break;
        }
        if (!this.CONST_IS_GRAPE_JS) {
            if (endpoint) {
                this.previewAction.workflowId = endpoint;
                this.previewAction.formId = workflowFormId;

                this.previewUpdateAction.workflowId = endpoint;
            } else {
                this.previewAction = null;
                this.previewUpdateAction = null;
            }
        }
    };

    attached() {
        const query = queryString({
            reference: {
                id: EditorContent.reference.id,
                modelId: EditorContent.reference.modelId,
            },
        });

        this.client
            .get('editor/dynamic-block-configuration?' + query)
            .then((dynamicBlockConfig) => {
                import(
                    /* webpackMode: "lazy" */
                    /* webpackChunkName: "editor" */
                    './utils/editor-loader'
                    ).then(async ({initializeEditor, shutdownEditor}) => {
                    this.shutdownEditor = shutdownEditor;

                    if (this.CONST_IS_GRAPE_JS) {
                        await loadGrapeJsEditor({
                            container: document.getElementById(
                                'gutenberg__editor_' + this.editorId
                            ),
                            id: this.id,
                            modelId: this.modelId,
                            dynamicBlockConfig: dynamicBlockConfig,
                            website: this.website,
                            data: this.data,
                            onLoadingChange: (isLoading) => {
                                this.grapeJsLoading = isLoading;
                            },
                        });
                    } else {
                        return initializeEditor(
                            this.editorId,
                            this.blocks,
                            this.content,
                            dynamicBlockConfig,
                            await this.websiteContextClient.getWebsite(this.data?.website)
                        );
                    }
                });

                window.addEventListener('keypress', this._keypress);
            });
    }

    detached() {
        if (this.CONST_IS_GRAPE_JS) {
            destroyGrapeJsEditor();
        } else {
            this.shutdownEditor(this.editorId);
        }
        window.removeEventListener('keypress', this._keypress);

        // @fixme force reload to avoid endless loop in Gutenberg
        UnsavedChanges.ignore = true;
        setTimeout(() => (window.location = window.location), 200);
    }

    cancel() {
        if (this.CONST_IS_GRAPE_JS) {
            setGrapeJsAutosave(false);
            autosaveStore.remove(this?.modelId, this?.id);
        }
        this.dialog.cancel();
    }

    async saveOnly() {
        if (this.CONST_IS_GRAPE_JS) {
            await this.saveProject(false);
        }
    }

    async save() {
        let content = ''
        if (this.CONST_IS_GRAPE_JS) {
            const isSavedSuccess = await this.saveProject();
            if(isSavedSuccess){
                return this.dialog.ok();
            }
            return;
        }

        content = EditorContent.load(this.editorId, true);
        if (!this.formId || !this.id || !this.property) {
            this.editor.data = content;
            return this.dialog.ok();
        }

        return this.client
            .put(this.formId + '/' + this.id, {[this.property]: content})
            .then(
                (response) => {
                    this.ea.publish('sio_form_post_submit', {
                        config: this.formConfig,
                        response,
                    });
                    this.flash.success('form.success');
                    this.dialog.ok();

                    return response;
                },
                (error) => {
                    console.error(error);
                    this.flash.error(
                        400 === error.status
                            ? 'Ungültige Formulardaten. Bitte kontrollieren Sie, ob alle Blöcke korrekt ausgefüllt sind.'
                            : 'form.error'
                    );
                }
            );
    }

    onBeforePreviewClick() {
        return {
            document: EditorContent.load(this.id, true),
        };
    }

    getActionContext(reference, previousContext, update) {
        if (this.previewAction.formId == null) {
            return reference;
        }

        if (update) {
            return Object.assign({}, reference, {
                data: {context: previousContext},
            });
        }

        return Object.assign({}, reference, {
            data: {formData: {context: previousContext}},
        });
    }

    onAfterSuccess(data) {
        this.previousContext = data[0]?.context;

        if (this.previewWindow == null || this.previewWindow.closed) {
            this.previewWindow = window.open(data[0].url);
        } else {
            this.previewWindow.location.href = data[0].url;
            this.previewWindow.focus();
        }
    }

    canDeactivate = ({wasCancelled}) => {
        if (wasCancelled && this.content !== EditorContent.content) {
            return this.dialogService
                .open({
                    viewModel: Confirm,
                    model: {
                        message:
                            'Wollen Sie wirklich ungespeicherte Änderungen verwerfen?',
                        title: this.label,
                        okMessage: 'Verwerfen',
                        cancelMessage: 'sio.cancel',
                    },
                })
                .whenClosed(({wasCancelled}) => !wasCancelled);
        }

        return true;
    };

    _keypress = ({keyCode}) => (27 === keyCode ? this.dialog.cancel() : true);

    _getGrapeJsSavePayload() {
        const editorProject = getGrapeJsProjectData();
        const localEntry = autosaveStore.read(this.modelId, this.id);
        return {
            ...(editorProject ?? localEntry?.data ?? {}),
            modelId: this.modelId,
            referenceId: this.id,
        };
    }

    async saveProject(clearCache = true) {
        const projectData = this._getGrapeJsSavePayload();

        if (!projectData?.pages?.length) {
            this.flash.error('form.error');
            return false;
        }

        try {
            await this.client.post('grapes-editor/save-content', projectData);

            setGrapeJsAutosave(false);
            autosaveStore.markServerSynced(this.modelId, this.id);

            if (clearCache) {
                autosaveStore.remove(this.modelId, this.id);
            } else {
                autosaveStore.write(this.modelId, this.id, projectData);
                setGrapeJsAutosave(true);
                autosaveStore.clearServerSynced(this.modelId, this.id);
            }

            this.flash.success('form.success');
            return true;
        } catch (error) {
            console.error('API Save Error:', error);
            this.flash.error('form.error');
            return false;
        }
    }

    async showPreviewGrapejs(data) {
        if (!this.CONST_IS_GRAPE_JS) {
            return;
        }
        const projectData = this._getGrapeJsSavePayload();
        try {
            await this.client
                .post('grapes-editor/save-preview', projectData)
                .then((response2) => {
                    if (this.previewWindow == null || this.previewWindow.closed) {
                        this.previewWindow = window.open(response2.data);
                    } else {
                        this.previewWindow.location.href = response2.data;
                        this.previewWindow.focus();
                    }
                });
        } catch (error) {
            console.error('API Save Error:', error);
        }
    }
}
